import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { ApiDigitalAuthPassword } from '../../services/auth/password.service';
import { Error } from '../../models/error.model';
import { GlobalSwal } from '../../styles/swal.style';
import { Message } from '../../models/message.model';
import { iconEye, iconEyeSlash } from '../../utils/icon.visibility.util';
import { legendInvalid, legendIconInvalid } from '../../utils/legend.data.util';
import { evaluatePasswordConfirmation, evaluatePasswordController } from '../../scripts/validate.password.script';
import { getError, getMessage } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { authApiKey, authClear } from '../../scripts/authentication.script';

import AppLegend from '../../components/element/Legend';

export interface ResetPageProps {};

let errorResponse: Error, messageResponse: Message;

const ResetPage: React.FunctionComponent<ResetPageProps> = props => {
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [typeInput, setTypeInput] = useState('password')
  const [password, setPassword] = useState({value: '', valid: false})
  const [passwordConfirmation, setPasswordConfirmation] = useState({value: '', valid: false})

  const submitReset = async (event: React.ChangeEvent <HTMLFormElement>) => {
    event.preventDefault()
    setLoadIndicator('on')

    if (password.valid && passwordConfirmation.valid) {
      let token: string = param.token ?? ''

      await ApiDigitalAuthPassword.postReset(authApiKey(), password.value, passwordConfirmation.value, token).then( (response) => {
        if (response.status === 200) {
          messageResponse = response.data
          authClear()

          GlobalSwal.fire({
            title: getMessage(messageResponse.message, lang.code),
            text: lang.labels.youHaveSuccessfullyResetYourPassword,
            icon: 'success',
            confirmButtonText: lang.labels.okGotIt
          }).then( () => {
            navigate('/auth/login', {replace: true})
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (password.value) legendInvalid('legend-password-required')
        if (passwordConfirmation.value) legendIconInvalid('input-password-confirmation', 'legend-confirmation-password-required')
        setLoadIndicator('off')
      })
    }
  }

  const handleChangePassword = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setPassword({ ...password, value: event.target.value })
  }

  const handleChangePasswordConfirmation = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setPasswordConfirmation({...passwordConfirmation , value: event.target.value})
  }

  const handleChangeVisibilityPassword = () => {
    if (typeInput === 'password') {
      setTypeInput('text')
      iconEye('icon-password-visibility')
    } else {
      setTypeInput('password')
      iconEyeSlash('icon-password-visibility')
    }
  }

  const validatePassword = () => {
    evaluatePasswordController(password, setPassword, 'legend-password-valid', 'legend-password-required', 'container-password-meter-control-1', 'container-password-meter-control-2', 'container-password-meter-control-3', 'container-password-meter-control-4', 'container-password-meter-control-5')
    evaluatePasswordConfirmation(true, password, passwordConfirmation, setPasswordConfirmation, 'input-password-confirmation', 'legend-confirmation-password-identical', 'legend-confirmation-password-valid', 'legend-confirmation-password-required')
  }

  const validatePasswordConfirmation = () => {
    evaluatePasswordConfirmation(false, password, passwordConfirmation, setPasswordConfirmation, 'input-password-confirmation', 'legend-confirmation-password-identical', 'legend-confirmation-password-valid', 'legend-confirmation-password-required')
  }

  useEffect( () => {
    setMounted(true)

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-lg-550px bg-body rounded shadow-sm p-10 mx-auto">
      <form className="form w-100" noValidate onSubmit={submitReset}>
        <div className="text-center mb-10">
          <h1 className="text-dark text-capitalize mb-3">{lang.labels.resetPassword}</h1>
          <div className="text-muted fw-bold fs-4">
            {lang.labels.enterNewPassword}
          </div>
        </div>
        <div className="fv-row mb-5">
          <div className="mb-0">
            <label className="form-label fs-6 fw-bolder text-dark">{lang.labels.password}</label>
            <div className="position-relative mb-2">
              <input id="input-password" className="form-control form-control-lg" name="password" autoComplete="off" type={typeInput} value={password.value} onChange={handleChangePassword} onKeyUp={validatePassword} onBlur={validatePassword} />
              <span className="btn btn-sm btn-icon position-absolute translate-middle top-50 end-0 me-n2">
                <i id="icon-password-visibility" className="bi bi-eye-slash fs-2" onClick={handleChangeVisibilityPassword}></i>
              </span>
            </div>
            <div className="d-flex align-items-center mb-2">
              <div id="container-password-meter-control-1" className="flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2"></div>
              <div id="container-password-meter-control-2" className="flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2"></div>
              <div id="container-password-meter-control-3" className="flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2"></div>
              <div id="container-password-meter-control-4" className="flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2"></div>
              <div id="container-password-meter-control-5" className="flex-grow-1 bg-secondary bg-active-success rounded h-5px"></div>
            </div>
          </div>
          <div className="text-muted">{lang.labels.use8OrMoreCharacters}</div>
          <AppLegend attribute={{check: password.valid, name: "password", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
        </div>
        <div className="fv-row mb-10">
          <label className="form-label fs-6 fw-bolder text-dark">{lang.labels.confirmPassword}</label>
          <input id="input-password-confirmation" className="form-control form-control-lg" type="password" autoComplete="off" name="password-confirmation" value={passwordConfirmation.value} onChange={handleChangePasswordConfirmation} onKeyUp={validatePasswordConfirmation} onBlur={validatePasswordConfirmation} />
          <AppLegend attribute={{check: passwordConfirmation.valid, name: "confirmation-password", index: null, sub_index: null}} container={{valid: true, required: true, identical: true, exists: false}}></AppLegend>
        </div>
        <div className="d-flex flex-wrap justify-content-center">
          <button className="btn btn-lg btn-primary me-4" type="submit" data-kt-indicator={loadIndicator}>
            <span className="indicator-label fw-bolder">{lang.labels.submit}</span>
            <span className="indicator-progress">
              {lang.labels.pleaseWait}
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          </button>
          <Link to="/auth/login" className="btn btn-lg btn-secondary fw-bolder">{lang.labels.cancel}</Link>
        </div>
      </form>
    </div>
  )
};

export default ResetPage;
